import { useCallback, useEffect, useState } from 'react';
import {
  DialogContent, DialogActions, Button, Alert, Box, CircularProgress, TextField, Typography,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useModalState } from '../../hooks/useModalState';
import { useDirtyFormGuard } from '../../hooks/useDirtyFormGuard';
import DraggableDialog from '../../components/DraggableDialog';
import { CopyId } from '../../components/CopyId';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import { tagsService, TAG_COLORS, contrastText } from '../../services/tags.service';

export default function CreateEditTag() {
  const { modalState, toggleModal } = useModalState('tag');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const close = useCallback(() => toggleModal(false), [toggleModal]);
  const { guardedClose, setDirty, setSubmitted } = useDirtyFormGuard(close);

  const { data: entity, isLoading, refetch } = useQuery({
    queryKey: ['tag', modalState.id],
    queryFn: () => tagsService.getById(modalState.id!),
    enabled: !!modalState.id && modalState.open,
  });

  const [name, setName] = useState('');
  const [color, setColor] = useState(TAG_COLORS[0]);
  const [status, setStatus] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [workspaceId, setWorkspaceId] = useState(defaultCreateWorkspace());

  useEffect(() => { if (modalState.id && modalState.open) refetch(); }, [modalState.id, modalState.open, refetch]);

  useEffect(() => {
    if (entity && modalState.open) {
      setName(entity.name);
      setColor(entity.color || TAG_COLORS[0]);
    } else if (!modalState.id && modalState.open) {
      setName('');
      setColor(TAG_COLORS[0]);
      setWorkspaceId(defaultCreateWorkspace());
    }
    setStatus(undefined);
  }, [entity, modalState.open, modalState.id]);

  const isEdit = modalState.reason === 'Update' && !!entity;
  const title = isEdit ? `Edit tag: ${entity?.name}` : 'Create Tag';

  const handleSave = async () => {
    if (!name.trim()) { setStatus('Tag name is required'); return; }
    setSubmitting(true);
    try {
      setSubmitted();
      if (isEdit) {
        await tagsService.update(entity!.id, { name: name.trim(), color });
      } else {
        await tagsService.create({ name: name.trim(), color, workspace_id: workspaceId } as any);
      }
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      close();
      enqueueSnackbar('Saved', { variant: 'success' });
    } catch (err: any) {
      setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DraggableDialog open={modalState.open} onClose={guardedClose} maxWidth="xs" fullWidth
      title={isLoading ? 'Loading...' : title} titleId="tag-dialog-title">
      {isLoading && modalState.id ? (
        <DialogContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      ) : (
        <>
          <DialogContent dividers>
            <CopyId id={entity?.id} show={isEdit} sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name" size="small" fullWidth autoFocus value={name}
                onChange={(e) => { setName(e.target.value); setDirty(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              />
              {!isEdit && (
                <WorkspaceField value={workspaceId} onChange={(id) => { setWorkspaceId(id); setDirty(true); }} />
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Color</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                  {TAG_COLORS.map((c) => (
                    <Box
                      key={c}
                      onClick={() => { setColor(c); setDirty(true); }}
                      sx={{
                        width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: (t) => `2px solid ${color === c ? t.palette.text.primary : 'transparent'}`,
                      }}
                    >
                      {color === c && <Check sx={{ fontSize: 16, color: contrastText(c) }} />}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">Preview:</Typography>
                <Box sx={{ px: 1.25, py: 0.25, borderRadius: 4, bgcolor: color, color: contrastText(color), fontSize: '0.8rem', fontWeight: 500 }}>
                  {name || 'tag'}
                </Box>
              </Box>
              {status && <Alert severity="error">{status}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={guardedClose}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={submitting || (!isEdit && !workspaceId)}>
              {submitting ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </>
      )}
    </DraggableDialog>
  );
}
